import { useEffect } from 'react';

import { useCheckFlag } from 'hooks/useCheckFlag';
import { t } from 'i18next';
import { useGetServiceBr, useGetVat } from 'queries';
import { FieldArrayWithId, useFormContext } from 'react-hook-form';

import { Grid } from '@mui/material';
import Box from '@mui/material/Box';

import {
  ControlledDatePicker,
  ControlledSelect,
  ControlledTextField
} from 'components/shared';
import { useFieldArrayContext } from 'components/shared/CRUD/components/InnerPage/hooks/useFieldArrayContext';
import useFindIndexFromFieldsArray from 'components/shared/CRUD/hooks/useFindIndexFromFieldsArray';
import { ControlledFlagCheckbox } from 'components/shared/ControlledFields/ControlledFlagCheckbox';
import ControlledNumericField from 'components/shared/ControlledFields/ControlledNumericField';

import { methodOptions } from '../../MethodSelectSection';

const getLastAddedFieldId = (fields: FieldArrayWithId[]) =>
  fields.slice(-1)[0]?.id;

const EditDetailsLine = ({ rowId }) => {
  const { control, setValue } = useFormContext();
  const { fields } = useFieldArrayContext();
  const fieldIndex = useFindIndexFromFieldsArray(
    rowId ?? getLastAddedFieldId(fields),
    fields
  );
  const { data: serviceBrOptions } = useGetServiceBr();
  const { data: vatOptions } = useGetVat();

  const isReqFlagContained = useCheckFlag(
    `details[${fieldIndex}].flags`,
    'SERVICE_BR_DEFAULT_FLAG'
  );

  useEffect(() => {
    if (isReqFlagContained) {
      setValue(`details[${fieldIndex}].serviceBrId`, null);
    }
  }, [isReqFlagContained]);

  return (
    <>
      <Box mb={2.5} width={'100%'} paddingTop={1}>
        <ControlledTextField
          name={`details[${fieldIndex}].description`}
          type="string"
          control={control}
          variant="outlined"
          label={t('surchargesMaintenance.form.description')}
        />
      </Box>
      <Box mb={2.5} width={'100%'} paddingTop={1}>
        <ControlledSelect
          control={control}
          name={`details[${fieldIndex}].method`}
          variant="outlined"
          label={t('surchargesMaintenance.form.method')}
          options={methodOptions}
        />
      </Box>
      <Box mb={2.5} width={'100%'}>
        <ControlledFlagCheckbox
          flag="REQUEST_BASED_SERVICE_FLAG"
          name={`details[${fieldIndex}].flags`}
          label={t(
            'surchargesMaintenance.form.flags.REQUEST_BASED_SERVICE_FLAG'
          )}
        />
      </Box>
      <Box mb={2.5} width={'100%'}>
        <Grid container paddingBottom={2}>
          <Grid item xs={12}>
            <ControlledFlagCheckbox
              flag="SERVICE_BR_DEFAULT_FLAG"
              name={`details[${fieldIndex}].flags`}
              label={t(
                'surchargesMaintenance.form.flags.SERVICE_BR_DEFAULT_FLAG'
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <ControlledSelect
              control={control}
              name={`details[${fieldIndex}].serviceBrId`}
              variant="outlined"
              label={t('surchargesMaintenance.form.serviceBrId')}
              options={(isReqFlagContained ? [] : serviceBrOptions) ?? []}
            />
          </Grid>
        </Grid>
      </Box>
      <Box mb={2.5} width={'100%'}>
        <Box display="flex" gap={2.5} maxWidth={440} paddingBottom={2}>
          <ControlledNumericField
            name={`details[${fieldIndex}].surcharge1Duty`}
            variant="outlined"
            label={t('surchargesMaintenance.form.surcharge1Duty')}
            step={1}
          />
          <ControlledNumericField
            name={`details[${fieldIndex}].surcharge1Nom`}
            variant="outlined"
            label={t('surchargesMaintenance.form.surcharge1Nom')}
            step={1}
          />
          <ControlledNumericField
            name={`details[${fieldIndex}].surcharge1Perc`}
            variant="outlined"
            label={t('surchargesMaintenance.form.surcharge1Perc')}
            step={1}
            defaultValue={0}
          />
        </Box>
        <Box display="flex" gap={2.5} maxWidth={440} paddingBottom={2}>
          <ControlledNumericField
            name={`details[${fieldIndex}].surcharge3Duty`}
            variant="outlined"
            label={t('surchargesMaintenance.form.surcharge3Duty')}
            step={1}
          />
          <ControlledNumericField
            name={`details[${fieldIndex}].surcharge3Nom`}
            variant="outlined"
            label={t('surchargesMaintenance.form.surcharge3Nom')}
            step={1}
          />
          <ControlledNumericField
            name={`details[${fieldIndex}].surcharge3Perc`}
            variant="outlined"
            label={t('surchargesMaintenance.form.surcharge3Perc')}
            step={1}
            defaultValue={0}
          />
        </Box>
      </Box>
      <Box mb={2.5} width={'100%'} display="flex" gap={2.5}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <ControlledDatePicker
              control={control}
              name={`details[${fieldIndex}].valid.from`}
              variant="outlined"
              label={t('validFrom')}
              fullWidth
            />
          </Grid>
          <Grid item xs={6}>
            <ControlledDatePicker
              control={control}
              name={`details[${fieldIndex}].valid.to`}
              variant="outlined"
              label={t('validTo')}
              fullWidth
            />
          </Grid>
        </Grid>
      </Box>
      <Box mb={2.5} width={'100%'} paddingTop={1}>
        <ControlledSelect
          control={control}
          name={`details[${fieldIndex}].vatSvc`}
          variant="outlined"
          label={t('surchargesMaintenance.form.vatSvc')}
          options={vatOptions ?? []}
        />
      </Box>
    </>
  );
};

export default EditDetailsLine;
